import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Avatar,
} from '@mui/material';
import { toast } from 'react-toastify';
import axios from 'axios';
import PropTypes from 'prop-types';
import { CheckCircleOutline, HighlightOff } from '@mui/icons-material';
import useAccount from '../../../_mock/account';

UserItem.propTypes = {
  user: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    emailVerified: PropTypes.bool.isRequired,
    phoneNumber: PropTypes.number.isRequired,
    promoCode: PropTypes.string.isRequired,
    referralCount: PropTypes.number.isRequired,
    role: PropTypes.string.isRequired,
    addressLine1: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    postalCode: PropTypes.string,
    country: PropTypes.string,
    gstNumber: PropTypes.string,
  }).isRequired,
};

export default function UserItem({ user }) {
  const account = useAccount();
  const token = localStorage.getItem('token');

  const initialData = useMemo(
    () => ({
      addressLine1: user.addressLine1 || '',
      city: user.city || '',
      state: user.state || '',
      postalCode: user.postalCode || '',
      country: user.country || '',
      gstNumber: user.gstNumber || '',
    }),
    [
      user.addressLine1,
      user.city,
      user.state,
      user.postalCode,
      user.country,
      user.gstNumber,
    ]
  );

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);


  const [formData, setFormData] = useState(initialData);
  const [isChanged, setIsChanged] = useState(false);
  const [errors, setErrors] = useState({ gstNumber: '' });

  // GST Validation Function
  const validateGST = (value) => {
    const gstRegex =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

    if (!value) return '';
    if (value.length !== 15) {
      return 'GSTIN must be exactly 15 characters';
    }
    if (!gstRegex.test(value)) {
      return 'Enter a valid GSTIN (e.g. 22AAAAA0000A1Z5)';
    }
    return '';
  };

  useEffect(() => {
    const hasChanged = Object.keys(initialData).some(
      (key) => formData[key] !== initialData[key]
    );
    setIsChanged(hasChanged);
  }, [formData, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === 'gstNumber') {
      // Allow only alphanumeric & uppercase
      updatedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

      const errorMsg = validateGST(updatedValue);
      setErrors((prev) => ({ ...prev, gstNumber: errorMsg }));
    }

    setFormData((prev) => ({ ...prev, [name]: updatedValue }));
  };

  const handleCancel = () => {
    setFormData(initialData);
    setErrors({ gstNumber: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const gstError = validateGST(formData.gstNumber);
    if (gstError) {
      setErrors({ gstNumber: gstError });
      return;
    }

    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const res = await axios.post(
        `${API_BASE_URL}/updateUserAddress`,
        formData,
        config
      );

      toast.success(res.data.message);
      setIsChanged(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Update failed');
    }
  };

  return (
    <Grid container spacing={3}>
      {/* User Profile Section */}
      <Grid item xs={12} md={3}>
        <Card
          elevation={3}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: 2,
          }}
        >
          <Avatar
            src={account.photoURL}
            alt={user.fullName}
            sx={{
              width: 150,
              height: 150,
              border: '4px solid #fff',
              boxShadow: 3,
            }}
          />
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold' }}>
            {user.fullName}
          </Typography>
        </Card>
      </Grid>

      {/* Profile Details Section */}
      <Grid item xs={12} md={9}>
        <Card elevation={3}>
          <CardHeader title="Personal Details" />
          <CardContent>
            <Table>
              <TableBody>
                {[
                  { label: 'Full Name', value: user.fullName },
                  { label: 'User Name', value: user.userName },
                  { label: 'Email', value: user.email },
                  {
                    label: 'Email Verified',
                    value: user.emailVerified ? (
                      <CheckCircleOutline sx={{ color: 'green' }} />
                    ) : (
                      <HighlightOff sx={{ color: 'red' }} />
                    ),
                  },
                  { label: 'Phone Number', value: user.phoneNumber },
                  { label: 'Promo Code', value: user.promoCode },
                  { label: 'Referral Count', value: user.referralCount },
                ].map((row, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {row.label}
                    </TableCell>
                    <TableCell>:</TableCell>
                    <TableCell
                      sx={{
                        wordBreak: 'break-all',
                        whiteSpace: 'normal',
                        maxWidth: { xs: '300px', sm: 'none' },
                      }}
                    >
                      {row.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </Grid>

      {/* Address Details Section */}
      <Grid item xs={12}>
        <Card elevation={3}>
          <CardHeader title="Address Details" />
          <CardContent>
            <form id="addressForm" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="GST Number"
                    name="gstNumber"
                    value={formData.gstNumber}
                    onChange={handleChange}
                    size="small"
                    inputProps={{ maxLength: 15 }}
                    error={Boolean(errors.gstNumber)}
                    helperText={errors.gstNumber}
                  />
                </Grid>

                {[
                  { name: 'addressLine1', label: 'Address Line 1' },
                  { name: 'city', label: 'City' },
                  { name: 'state', label: 'State' },
                  { name: 'postalCode', label: 'Postal Code' },
                  { name: 'country', label: 'Country' },
                ].map((field) => (
                  <Grid item xs={12} md={4} key={field.name}>
                    <TextField
                      fullWidth
                      label={field.label}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      size="small"
                    />
                  </Grid>
                ))}
              </Grid>

              <Grid container justifyContent="center" spacing={2} sx={{ mt: 2 }}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    form="addressForm"
                    disabled={!isChanged}
                  >
                    Update Address
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="error"
                    type="button"
                    onClick={handleCancel}
                    disabled={!isChanged}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
